// FinanceNonProfit — the 501(c)(4) operating-funds dashboard (Truist bank +
// QuickBooks Online books). Kept STRICTLY separate from the levy (PWC
// appropriation) view — the two fund types are never commingled.
//
// DCVFD is a 501(c)(4) social-welfare org (NOT a 501(c)(3) charity), so this is
// the non-profit operating side, not a "charitable" fund.
//
// Headline is Cash Available (total bank − restricted 1552 Payroll); the total
// incl. payroll is shown as a smaller caption. Below: per-account bank overview
// (restricted flagged), a monthly income/expense/net time series with an
// own-revenue vs PWC-reimbursement split, current-period revenue-by-source and
// expense-by-category breakdowns, and an A/R + Billable-to-County card.
//
// Every figure comes from the QBO snapshot endpoints; nothing is faked. Until a
// given snapshot is synced the corresponding section degrades to an honest
// "syncing…" / empty state.
//
// NET OPERATING IS CURRENTLY WITHHELD — see `netKpiState` below. QuickBooks
// books the county payroll advance as a credit against expense account 55601
// (Payroll — Employee Subsidy) instead of against the payroll liability, which
// drives the QBO expense total negative and makes income − expenses read as a
// large positive surplus. Finance (Frank, 2026-08-01) ruled the figure is held
// off the board until the bookkeeper explains/reclassifies it. We show the tile
// with an honest "Withheld" and a visible reason — we do not drop it silently
// and we do not substitute a different computed number.

import { useState } from "react";
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import {
  useNonprofitSummary,
  useNonprofitIncomeBySource,
  useNonprofitExpensesByCategory,
  useNonprofitTimeseries,
  type NonprofitBankAccount,
  type NonprofitIncomeRow,
  type NonprofitExpenseRow,
} from "../hooks/useFinance";
import KPICard from "../components/dashboard/KPICard";
import ChartCard from "../components/dashboard/ChartCard";
import FundBadge from "../components/finance/FundBadge";
import InfoTip from "../components/shared/InfoTip";
import PageIntro from "../components/shared/PageIntro";
import PageShell from "../components/shared/PageShell";
import PageHeader from "../components/shared/PageHeader";
import {
  asNum,
  usd0,
  usdC,
  TOOLTIP_PROPS,
  MUTED,
  GRID,
  COLORS,
} from "../components/finance/charts";
import {
  BTN_BRAND,
  FetchErrorBanner,
  TH,
  THR,
  TD,
  TDR,
} from "../components/finance/ui";
import Banner from "../components/shared/Banner";
import { fundForFy, currentFiscalYear } from "../lib/fiscal";

// ── Net Operating hold (finance control C-4, no-downgrade / no-bad-number) ────
//
// Set by finance, cleared by finance — not by a code change made on inference.
// While true, the Net Operating KPI is withheld from the board with a visible
// reason. Clear this ONLY when the 55601 contra-credit has been explained and,
// if required, reclassified out of the expense side.
export const NET_KPI_HOLD = true;

/** Short line shown on the tile itself. */
export const NET_KPI_HOLD_SUBTITLE =
  "Withheld pending explanation — not a break-even";

/** The board-facing explanation rendered beside the KPI row. */
export const NET_KPI_HOLD_NOTE =
  "QuickBooks currently books the county payroll advance as a credit against " +
  "expense account 55601 (Payroll — Employee Subsidy) rather than against the " +
  "payroll liability. That pushes the reported expense total negative, so " +
  "income − expenses would read as a large positive surplus that the " +
  "department did not earn. The figure is withheld until the bookkeeper " +
  "explains and, if required, reclassifies the entry — it is not zero and not " +
  "a break-even. The Income and Expenses tiles above, and the Net Operating " +
  "line in the monthly chart below, are shown exactly as QuickBooks reports " +
  "them and carry the same distortion.";

export type NetKpiState =
  | { withheld: false; value: number | null }
  | { withheld: true };

/**
 * Decide whether the Net Operating KPI may be shown as a number.
 *
 * - No figure at all (not connected / not synced) → not withheld, renders the
 *   usual honest "—". "Withheld" must never be used to describe missing data.
 * - Finance hold active → withheld.
 * - Defence in depth, independent of the flag: a negative period expense total
 *   means the expense side carries a contra-credit, so income − expenses is not
 *   an operating result and must not reach the board as one.
 */
export function netKpiState(
  net: number | null | undefined,
  expenseTotal: number | null | undefined,
): NetKpiState {
  if (net == null) return { withheld: false, value: null };
  if (NET_KPI_HOLD) return { withheld: true };
  if (expenseTotal != null && expenseTotal < 0) return { withheld: true };
  return { withheld: false, value: net };
}

/**
 * The Net Operating KPI tile. Renders "Withheld" (never a number, never a
 * substituted figure) whenever `netKpiState` says the figure is held.
 */
export function NetOperatingCard({
  net,
  expenseTotal,
  isLoading = false,
}: {
  net: number | null | undefined;
  expenseTotal: number | null | undefined;
  isLoading?: boolean;
}) {
  const state = netKpiState(net, expenseTotal);
  return (
    <KPICard
      label="Net Operating"
      value={
        state.withheld
          ? "Withheld"
          : state.value == null
            ? "—"
            : usd0(state.value)
      }
      subtitle={
        state.withheld
          ? NET_KPI_HOLD_SUBTITLE
          : "Income − expenses (not QBO Net Income)"
      }
      accent={
        state.withheld
          ? "amber"
          : state.value != null && state.value < 0
            ? "red"
            : "neutral"
      }
      isLoading={isLoading}
    />
  );
}

/**
 * The visible reason the tile is blank. Renders nothing when the figure is not
 * withheld — a withheld KPI is never allowed to appear without this note.
 */
export function NetOperatingWithheldNote({
  net,
  expenseTotal,
  isLoading = false,
}: {
  net: number | null | undefined;
  expenseTotal: number | null | undefined;
  isLoading?: boolean;
}) {
  if (isLoading || !netKpiState(net, expenseTotal).withheld) return null;
  return (
    <Banner variant="warn" title="Net Operating is withheld from this view">
      {NET_KPI_HOLD_NOTE}
    </Banner>
  );
}

function QuickBooksIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="40"
      height="40"
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <circle cx="12" cy="12" r="9.5" />
      <path d="M12 7v7a2.5 2.5 0 0 1-2.5 2.5" />
      <path d="M12 17v-7a2.5 2.5 0 0 1 2.5-2.5" />
    </svg>
  );
}

// Map the ?qbo_error= reason the OAuth callback redirects back with to a
// human-readable line. Unknown reasons fall through to the raw code.
function qboErrorMessage(reason: string): string {
  switch (reason) {
    case "access_denied":
      return "Authorization was declined in QuickBooks.";
    case "invalid_state":
      return "The authorization session expired. Please try connecting again.";
    case "missing_params":
      return "QuickBooks did not return the expected authorization details.";
    case "exchange_failed":
      return "Connecting to QuickBooks failed during token exchange. Please retry.";
    default:
      return `QuickBooks authorization failed (${reason}).`;
  }
}

export default function FinanceNonProfit() {
  const { data, isLoading, error } = useNonprofitSummary();
  const { data: income } = useNonprofitIncomeBySource();
  const { data: expenses } = useNonprofitExpensesByCategory();
  const { data: timeseries } = useNonprofitTimeseries();

  const connected = data?.connected ?? false;
  const connectUrl = data?.connect_url ?? "/api/finance/qbo/connect";

  // Surface the OAuth round-trip outcome from the callback redirect query.
  const params = new URLSearchParams(window.location.search);
  const justConnected = params.get("connected") === "1";
  const qboError = params.get("qbo_error");

  // Money KPIs render null-safe: null → "—" (we never invent a number).
  const money = (n: number | null | undefined) => (n == null ? "—" : usd0(n));

  const cashAvailable = data?.cash_available ?? null;
  const cashTotal = data?.cash_total ?? null;
  const cashRestricted = data?.cash_restricted ?? null;
  const incomeTotal = data?.income_total ?? null;
  const expenseTotal = data?.expense_total ?? null;
  const net = data?.net ?? null;

  const haveData =
    cashTotal != null || incomeTotal != null || expenseTotal != null;

  return (
    <PageShell>
      <div>
        <PageHeader
          eyebrow="FINANCE"
          title="Non-Profit Funds"
          subtitle={
            <span className="inline-flex flex-wrap items-center gap-1">
              <span>501(c)(4)</span>
              <InfoTip term="501c" />
              <span>
                operating books — Truist bank + QuickBooks Online.{" "}
                {connected && data?.as_of ? `As of ${data.as_of}.` : ""}{" "}
                Separate from the levy appropriation — these funds are never
                commingled.
              </span>
            </span>
          }
          headerRight={<FundBadge kind="nonprofit" />}
        />
        <PageIntro>
          Our non-profit's own money — the 501(c)(4) social-welfare side's bank
          balances and income/expenses, kept completely separate from the county
          levy funds. The big number is cash available after carving out the
          payroll liability (cash owed to employees) — the other fund balances
          are unrestricted and count as available.
        </PageIntro>
      </div>

      <FetchErrorBanner error={error} what="non-profit funds" />

      {qboError && (
        <div className="rounded-lg border border-status-red/30 bg-status-red/10 px-4 py-3 text-sm text-status-red">
          {qboErrorMessage(qboError)}
        </div>
      )}
      {justConnected && !qboError && (
        <div className="rounded-lg border border-status-green/30 bg-status-green/10 px-4 py-3 text-sm text-status-green">
          QuickBooks connected. Run a sync to populate the non-profit dashboard.
        </div>
      )}

      {/* ── Headline KPIs ─────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <CashAvailableCard
          available={cashAvailable}
          total={cashTotal}
          restricted={cashRestricted}
          isLoading={isLoading}
        />
        <KPICard
          label="Income"
          value={money(incomeTotal)}
          subtitle="Period revenue (QBO)"
          accent="green"
          isLoading={isLoading}
        />
        <KPICard
          label="Expenses"
          value={money(expenseTotal)}
          subtitle="Period expenses (QBO)"
          accent="amber"
          isLoading={isLoading}
        />
        <NetOperatingCard
          net={net}
          expenseTotal={expenseTotal}
          isLoading={isLoading}
        />
      </div>

      <NetOperatingWithheldNote
        net={net}
        expenseTotal={expenseTotal}
        isLoading={isLoading}
      />

      {/* ── Not-connected / connected-no-data empty states ─────────────────── */}
      {!isLoading && !connected && <ConnectCard connectUrl={connectUrl} />}
      {!isLoading && connected && !haveData && (
        <AwaitingSyncCard connectedBy={data?.connected_by ?? null} />
      )}

      {connected && haveData && (
        <>
          {/* ── Bank accounts overview ─────────────────────────────────────── */}
          <BankAccountsCard
            accounts={data?.bank_accounts ?? []}
            total={cashTotal}
            restricted={cashRestricted}
            available={cashAvailable}
          />

          {/* ── Money over time ────────────────────────────────────────────── */}
          <MoneyOverTimeCard
            available={timeseries?.available ?? false}
            months={timeseries?.months ?? []}
          />

          {/* ── Revenue by source + expenses by category ───────────────────── */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <RevenueBySourceCard
              available={income?.available ?? false}
              rows={income?.rows ?? []}
              ownTotal={income?.own_total ?? 0}
              pwcTotal={income?.pwc_total ?? 0}
            />
            <ExpensesByCategoryCard
              available={expenses?.available ?? false}
              rows={expenses?.rows ?? []}
              total={expenses?.total ?? 0}
              pwcTotal={income?.pwc_total ?? null}
            />
          </div>

          {/* ── Receivables ────────────────────────────────────────────────── */}
          <ReceivablesCard arTotal={data?.ar_total ?? null} />
        </>
      )}

      <p className="text-xs text-theme-muted">
        Non-profit funds are the 501(c)(4) operating books (Truist bank,
        QuickBooks Online) — entirely separate from the levy appropriation (fund{" "}
        {fundForFy(currentFiscalYear())}, PWC/Mobius). The PWC reimbursement
        line is a passthrough of county money, not the non-profit&apos;s own
        revenue, and is shown separately throughout. The two fund types are
        never combined in any total.
      </p>
    </PageShell>
  );
}

// ── Cash Available headline (with the total-incl-payroll sub-line) ────────────
function CashAvailableCard({
  available,
  total,
  restricted,
  isLoading,
}: {
  available: number | null;
  total: number | null;
  restricted: number | null;
  isLoading: boolean;
}) {
  const subtitle =
    total != null && restricted != null && restricted > 0
      ? `${usd0(total)} total, less ${usd0(restricted)} payroll set-aside`
      : total != null
        ? `${usd0(total)} total bank balances`
        : "Operating cash, less payroll set-aside";
  return (
    <KPICard
      label={
        <span className="inline-flex items-center gap-1">
          Cash Available <InfoTip term="restricted_funds" />
        </span>
      }
      value={available == null ? "—" : usd0(available)}
      subtitle={subtitle}
      accent="neutral"
      isLoading={isLoading}
    />
  );
}

// ── Bank accounts overview ────────────────────────────────────────────────────
function BankAccountsCard({
  accounts,
  total,
  restricted,
  available,
}: {
  accounts: NonprofitBankAccount[];
  total: number | null;
  restricted: number | null;
  available: number | null;
}) {
  if (accounts.length === 0) {
    return (
      <ChartCard
        title="Bank Accounts"
        subtitle="Account-level balances will appear once the chart of accounts syncs"
        empty
        minHeight={96}
      >
        <p className="text-sm text-theme-muted">
          No per-account balances yet — run a QuickBooks sync to pull the chart
          of accounts.
        </p>
      </ChartCard>
    );
  }
  return (
    <ChartCard
      title="Bank Accounts"
      subtitle="Truist / PayPal balances per QuickBooks — lines foot to Total bank"
    >
      <div className="overflow-x-auto">
        <table className="min-w-full">
          <thead>
            <tr className="border-b border-theme-subtle text-left">
              <th className={TH}>Account</th>
              <th className={TH}>No.</th>
              <th className={THR}>Balance</th>
            </tr>
          </thead>
          <tbody>
            {accounts.map((a) => (
              <tr
                key={`${a.acct_num}:${a.name}`}
                className={`hover:bg-theme-overlay/30 transition-colors ${a.reconciling ? "text-theme-muted italic" : ""}`}
              >
                <td className={TD}>
                  <span
                    className={
                      a.reconciling
                        ? "text-theme-muted"
                        : "text-theme-primary font-medium"
                    }
                  >
                    {a.name}
                  </span>
                  {a.restricted && (
                    <span className="ml-2 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide bg-status-amber/15 text-status-amber">
                      payroll set-aside
                    </span>
                  )}
                </td>
                <td className={`${TD} tabular-nums text-theme-muted`}>
                  {a.acct_num || "—"}
                </td>
                <td className={TDR}>{usd0(a.balance)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t border-theme-subtle">
              <td className={`${TD} font-semibold text-theme-primary`}>
                Total bank balances
              </td>
              <td className={TD} />
              <td className={`${TDR} font-semibold text-theme-primary`}>
                {total == null ? "—" : usd0(total)}
              </td>
            </tr>
            {restricted != null && restricted > 0 && (
              <>
                <tr>
                  <td className={`${TD} text-theme-muted`}>
                    Less payroll set-aside (board-designated)
                  </td>
                  <td className={TD} />
                  <td className={`${TDR} text-theme-muted`}>
                    −{usd0(restricted)}
                  </td>
                </tr>
                <tr className="border-t border-theme-subtle">
                  <td className={`${TD} font-semibold text-theme-primary`}>
                    Cash available, net of payroll set-aside
                  </td>
                  <td className={TD} />
                  <td className={`${TDR} font-semibold text-status-green`}>
                    {available == null ? "—" : usd0(available)}
                  </td>
                </tr>
              </>
            )}
          </tfoot>
        </table>
      </div>
      <p className="mt-3 text-xs text-theme-muted leading-relaxed">
        Payroll (1552) holds cash owed for employee payroll &amp; admin — a
        liability carved out of cash available. The other balances (Boot Fund,
        Fundraising IDA, DeSanto Hall, CDs) are the department&rsquo;s own
        unrestricted funds and <strong>are</strong> included in cash available.
        Any &ldquo;unallocated (pending reclass)&rdquo; line is money posted
        directly to a parent account in QuickBooks that needs re-coding to a
        sub-account — bookkeeping cleanup that changes no total.
      </p>
    </ChartCard>
  );
}

// ── Money over time (monthly income / expense / net + own-vs-PWC split) ───────
const TS_SERIES = {
  income: { key: "income", name: "Income", color: COLORS[1] },
  expense: { key: "expense", name: "Expenses", color: COLORS[3] },
  net: { key: "net", name: "Net Operating", color: COLORS[0] },
} as const;

function MoneyOverTimeCard({
  available,
  months,
}: {
  available: boolean;
  months: Array<Record<string, unknown>>;
}) {
  const [view, setView] = useState<"totals" | "source">("totals");
  const empty = !available || months.length === 0;
  return (
    <ChartCard
      title="Money over time"
      subtitle="Monthly income, expenses and net — and the own-revenue vs PWC-reimbursement split"
      headerRight={
        !empty ? (
          <div className="flex items-center gap-1">
            {(["totals", "source"] as const).map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => setView(v)}
                className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors min-h-[28px] ${
                  view === v
                    ? "bg-[#0E403A] text-white shadow-sm"
                    : "bg-theme-overlay text-theme-muted hover:text-theme-primary"
                }`}
              >
                {v === "totals"
                  ? "Income / Expense / Net"
                  : "Revenue by source"}
              </button>
            ))}
          </div>
        ) : undefined
      }
      empty={empty}
      minHeight={320}
    >
      {empty ? (
        <p className="text-sm text-theme-muted">
          Monthly history is syncing — the time series appears once the monthly
          Profit &amp; Loss snapshot has been pulled from QuickBooks.
        </p>
      ) : view === "totals" ? (
        <ResponsiveContainer width="100%" height={320}>
          <LineChart
            data={months}
            margin={{ top: 16, right: 16, bottom: 4, left: 8 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke={GRID} />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: MUTED }} />
            <YAxis
              tickFormatter={(v) => usdC(Number(v))}
              tick={{ fontSize: 11, fill: MUTED }}
              width={56}
            />
            <Tooltip
              formatter={(v, name) => [usd0(Number(v)), name]}
              {...TOOLTIP_PROPS}
            />
            <Legend
              wrapperStyle={{ fontSize: 12, paddingTop: 8 }}
              iconType="plainline"
            />
            <Line
              type="monotone"
              dataKey={TS_SERIES.income.key}
              name={TS_SERIES.income.name}
              stroke={TS_SERIES.income.color}
              strokeWidth={2.5}
              dot={{ r: 2 }}
              activeDot={{ r: 5 }}
            />
            <Line
              type="monotone"
              dataKey={TS_SERIES.expense.key}
              name={TS_SERIES.expense.name}
              stroke={TS_SERIES.expense.color}
              strokeWidth={2.5}
              dot={{ r: 2 }}
              activeDot={{ r: 5 }}
            />
            <Line
              type="monotone"
              dataKey={TS_SERIES.net.key}
              name={TS_SERIES.net.name}
              stroke={TS_SERIES.net.color}
              strokeWidth={2.5}
              dot={{ r: 2 }}
              activeDot={{ r: 5 }}
            />
          </LineChart>
        </ResponsiveContainer>
      ) : (
        <ResponsiveContainer width="100%" height={320}>
          <AreaChart
            data={months}
            margin={{ top: 16, right: 16, bottom: 4, left: 8 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke={GRID} />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: MUTED }} />
            <YAxis
              tickFormatter={(v) => usdC(Number(v))}
              tick={{ fontSize: 11, fill: MUTED }}
              width={56}
            />
            <Tooltip
              formatter={(v, name) => [usd0(Number(v)), name]}
              {...TOOLTIP_PROPS}
            />
            <Legend
              wrapperStyle={{ fontSize: 12, paddingTop: 8 }}
              iconType="plainline"
            />
            <Area
              type="monotone"
              dataKey="own_income"
              name="Own revenue"
              stackId="1"
              stroke={COLORS[0]}
              fill={COLORS[0]}
              fillOpacity={0.5}
            />
            <Area
              type="monotone"
              dataKey="pwc_income"
              name="PWC reimbursement (passthrough)"
              stackId="1"
              stroke={COLORS[2]}
              fill={COLORS[2]}
              fillOpacity={0.35}
            />
          </AreaChart>
        </ResponsiveContainer>
      )}
    </ChartCard>
  );
}

// ── Revenue by source (own-source separated from PWC passthrough) ─────────────
function RevenueBySourceCard({
  available,
  rows,
  ownTotal,
  pwcTotal,
}: {
  available: boolean;
  rows: NonprofitIncomeRow[];
  ownTotal: number;
  pwcTotal: number;
}) {
  const own = rows.filter((r) => !r.is_pwc);
  const pwc = rows.filter((r) => r.is_pwc);
  return (
    <div className="glass rounded-2xl p-5 space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-theme-primary">
          Revenue by source
        </h2>
        <p className="text-xs text-theme-muted mt-0.5">
          Current period. Own-source revenue (donations, fund drive, hall) is
          kept separate from the PWC county reimbursement passthrough.
        </p>
      </div>
      {!available ? (
        <p className="text-sm text-theme-muted">
          Income breakdown is syncing from QuickBooks.
        </p>
      ) : (
        <>
          <Breakdown
            heading={`Own-source revenue (${usd0(ownTotal)})`}
            rows={own.map((r) => ({ label: r.source, value: r.amount }))}
            emptyText="No own-source revenue recorded."
          />
          {pwc.length > 0 && (
            <Breakdown
              heading={`PWC reimbursements — passthrough (${usd0(pwcTotal)})`}
              rows={pwc.map((r) => ({
                label: r.source,
                value: r.amount,
                tag: "passthrough" as const,
              }))}
              emptyText=""
            />
          )}
        </>
      )}
    </div>
  );
}

// ── Expenditures (largely levy-reimbursed) ────────────────────────────────────
// The 501(c)(4) pays vendors and PWC reimburses most of it via the levy (the
// "PWC reimbursement" passthrough income). So this expense total is NOT the
// org's own non-levy spending — it's mostly levy expenditures the County pays
// back. We surface the genuine own-funded remainder = expenses − reimbursement.
function ExpensesByCategoryCard({
  available,
  rows,
  total,
  pwcTotal,
}: {
  available: boolean;
  rows: NonprofitExpenseRow[];
  total: number;
  pwcTotal: number | null;
}) {
  // Own-funded / non-levy spend = expenses not covered by the PWC reimbursement.
  // Floor at 0; only show when we actually have the reimbursement figure.
  const haveReimbursement = pwcTotal != null && pwcTotal > 0;
  const ownFunded = haveReimbursement ? Math.max(0, total - pwcTotal) : null;
  return (
    <div className="glass rounded-2xl p-5 space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-theme-primary">
          Expenditures (largely levy-reimbursed)
        </h2>
        <p className="text-xs text-theme-muted mt-0.5">
          Where the money flows{total > 0 ? ` (${usd0(total)} total)` : ""} —
          current period, by category. Most of this is reimbursed by the County
          via the levy; it is not the org&apos;s own non-levy spending.
        </p>
      </div>
      {!available ? (
        <p className="text-sm text-theme-muted">
          Expense breakdown is syncing from QuickBooks.
        </p>
      ) : (
        <>
          {ownFunded != null && (
            <div className="rounded-lg border border-theme-subtle bg-theme-overlay/40 px-4 py-3 space-y-1.5">
              <div className="flex items-baseline justify-between gap-3 text-sm">
                <span className="text-theme-muted">Total expenses</span>
                <span className="tabular-nums text-theme-secondary">
                  {usd0(total)}
                </span>
              </div>
              <div className="flex items-baseline justify-between gap-3 text-sm">
                <span className="text-theme-muted">
                  Less PWC reimbursement (levy passthrough)
                </span>
                <span className="tabular-nums text-theme-muted">
                  −{usd0(pwcTotal as number)}
                </span>
              </div>
              <div className="flex items-baseline justify-between gap-3 border-t border-theme-subtle/50 pt-1.5">
                <span className="text-sm font-semibold text-theme-primary">
                  Own-funded (non-levy) spend
                </span>
                <span className="tabular-nums text-base font-bold text-theme-primary">
                  {usd0(ownFunded)}
                </span>
              </div>
              <p className="text-xs text-theme-muted">
                The org pays vendors and the County reimburses via the levy;
                only the unreimbursed remainder is the 501(c)(4)&apos;s own
                non-levy spending (covered by own-source revenue).
              </p>
            </div>
          )}
          <Breakdown
            heading="Where the money flows — by category"
            rows={rows.map((r) => ({ label: r.category, value: r.amount }))}
            emptyText="No expenses recorded this period."
          />
        </>
      )}
    </div>
  );
}

// ── Receivables / Billable to County ──────────────────────────────────────────
function ReceivablesCard({ arTotal }: { arTotal: number | null }) {
  return (
    <div className="glass rounded-2xl p-5">
      <div className="flex items-baseline justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-theme-primary">
            Accounts Receivable
          </h2>
          <p className="text-xs text-theme-muted mt-0.5">
            Owed to the non-profit, including amounts billable to the county.
          </p>
        </div>
        <span className="text-2xl font-bold tabular-nums text-theme-primary">
          {arTotal == null ? "—" : usd0(arTotal)}
        </span>
      </div>
    </div>
  );
}

// ── Shared ranked breakdown list (label · amount + bar) ───────────────────────
interface BreakdownItem {
  label: string;
  value: number;
  tag?: "passthrough";
}
function Breakdown({
  heading,
  rows,
  emptyText,
}: {
  heading: string;
  rows: BreakdownItem[];
  emptyText: string;
}) {
  const max = rows.reduce((m, r) => Math.max(m, Math.abs(r.value)), 0);
  return (
    <div className="space-y-2">
      <h3 className="text-xs font-semibold uppercase tracking-wider text-theme-muted">
        {heading}
      </h3>
      {rows.length === 0 ? (
        emptyText ? (
          <p className="text-sm text-theme-muted">{emptyText}</p>
        ) : null
      ) : (
        <ul className="space-y-1.5">
          {rows.map((r, i) => {
            const pct =
              max > 0 ? Math.max(2, (Math.abs(r.value) / max) * 100) : 0;
            return (
              <li key={`${r.label}:${i}`} className="text-sm">
                <div className="flex items-baseline justify-between gap-3">
                  <span className="truncate text-theme-primary" title={r.label}>
                    {r.label}
                    {r.tag === "passthrough" && (
                      <span className="ml-1.5 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide bg-[#8b5cf6]/15 text-[#8b5cf6]">
                        passthrough
                      </span>
                    )}
                  </span>
                  <span className="shrink-0 tabular-nums font-semibold text-theme-primary">
                    {usd0(asNum(r.value))}
                  </span>
                </div>
                <div className="mt-1 h-1 rounded-full bg-theme-overlay overflow-hidden">
                  <div
                    className="h-full rounded-full bg-dcvfd-accent/60"
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

// ── Empty states ──────────────────────────────────────────────────────────────
function ConnectCard({ connectUrl }: { connectUrl: string }) {
  return (
    <ChartCard
      title="QuickBooks Online"
      subtitle="Connect the non-profit books to populate this dashboard"
      empty
      minHeight={120}
    >
      <div className="flex flex-col items-center text-center gap-4 py-8">
        <div className="text-[#8b5cf6]">
          <QuickBooksIcon />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-theme-primary">
            QuickBooks not connected
          </h3>
          <p className="text-sm text-theme-muted mt-1 max-w-md">
            Connect QuickBooks Online to see non-profit funds — cash available,
            bank accounts, income, expenses, and the monthly trend for the
            501(c)(4) operating books. Until then, no figures are shown.
          </p>
        </div>
        <a
          href={connectUrl}
          className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold ${BTN_BRAND}`}
        >
          Connect QuickBooks →
        </a>
        <p className="text-xs text-theme-muted">
          One-time authorization (admin). Tokens are stored encrypted; only
          roll-up totals are shown here.
        </p>
      </div>
    </ChartCard>
  );
}

function AwaitingSyncCard({ connectedBy }: { connectedBy: string | null }) {
  return (
    <ChartCard
      title="QuickBooks connected"
      subtitle="Awaiting the first report sync"
      empty
      minHeight={120}
    >
      <div className="flex flex-col items-center text-center gap-3 py-8">
        <div className="text-status-green">
          <QuickBooksIcon />
        </div>
        <h3 className="text-lg font-semibold text-theme-primary">
          Connected — no report data yet
        </h3>
        <p className="text-sm text-theme-muted max-w-md">
          QuickBooks Online is connected
          {connectedBy ? ` (by ${connectedBy})` : ""}. The first Profit &amp;
          Loss / Balance Sheet sync hasn&apos;t landed yet — the dashboard
          populates once it does.
        </p>
      </div>
    </ChartCard>
  );
}
